// Toast notifications. State-driven: openNotification pushes a record
// into `root.state.notifications` and schedules its auto-dismiss via
// setTimeout. GlobalNotification renders children directly from that
// array — exit animation runs first (`_exiting: true` flag triggers
// fadeOutDown), then the entry is dropped from the array.
//
// Lifecycle per toast:
//   1. openNotification → push { id, ...obj } to state.notifications,
//      schedule dismissal at `duration` ms.
//   2. user mouseover → pauseNotification(id) — clear timer, remember
//      remaining time. mouseout → resumeNotification(id) — restart for
//      remaining time.
//   3. timer fires → flip `_exiting: true` on the toast in state →
//      GlobalNotification's reactive `animation` prop swaps to
//      `fadeOutDown`. After EXIT_MS we drop the toast from the array.
//
// Standalone surfaces (auth pages on the auth-bounce path) reach this
// before the shell mounts; we degrade to console logs so the caller
// doesn't crash. The shell-level GlobalNotification (workspace/app.js)
// means signed-in users see real toasts.
//
// Additive `obj` fields (all optional, all just ride the `{ ...obj }` spread
// straight onto the record — GlobalNotification.js is what actually reads
// them):
//   sound: true                        — chime via el.call('playNotifySound')
//                                         once this call clears the de-dup
//                                         guard below.
//   icon: '<icon name>'                — overrides the toast's IconText.Icon
//                                         (defaults per `type`, see
//                                         GlobalNotification.js).
//   action: { call: 'fn', args: [...] } — called on toast click, AFTER
//                                         href routing, BEFORE dismiss.
//                                         Serializable only — never put a
//                                         function reference into state.
//                                         `fn` is resolved in the context of
//                                         the element that RAISED the toast
//                                         (see runNotificationAction).

import { isLoudDevRuntime } from '../functions/isLoudDevRuntime.js'

const _timers = new Map() // id → { timer, startedAt, remaining, paused, state }

// WHERE A TOAST'S ACTION RUNS (DESIGN-WL-SHARED-TOAST-ACTION-CALLS-MODULE-FUNCTIONS-1).
// The toast renders in the SHELL, but an installed workspace module (CRM)
// registers its functions in its OWN context — a CRM "Undo" named a function
// the shell's toast element could not see, and `el.call` of an unknown name
// returns nothing: the Undo silently did nothing. The action is resolved at
// raise time against the raising element (`this` of openNotification — a
// module's `el.call('openNotification', …)` passes its own element); when that
// element can call it, the action runs through that element on click, so the
// function gets the module element as `this`. Otherwise it runs through the
// toast (the shell), exactly as before. The element stays OUT of state (the
// record is serializable): it lives here, beside the timers, and leaves with
// the toast.
const _origins = new Map() // id → the raising element that can run the action
const _warnedNames = new Set()

// The framework's own `call` lookup order (utils → functions → methods →
// snippets).
const _canCall = (el, name) => {
  const c = el && el.context
  if (!c || !name) return false
  const fn = c.utils?.[name] || c.functions?.[name] || c.methods?.[name] || c.snippets?.[name]
  return typeof fn === 'function'
}

// One dev warning per unknown action name — never a throw, never a repeat.
const _warnUnknownAction = (name) => {
  if (_warnedNames.has(name)) return
  _warnedNames.add(name)
  if (!isLoudDevRuntime()) return
  console.warn(
    `[notification] the toast action "${name}" names no function where it was raised or in the shell — the toast only closes`
  )
}

const _forget = (id) => {
  _origins.delete(id)
}

const EXIT_MS = 300 // must match the fadeOutDown animationDuration on the toast

const _genId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

const _logFallback = (obj) => {
  if (typeof console === 'undefined') return
  const tag = `[${obj.type || 'notification'}] ${obj.title || ''}`
  const msg = obj.message ? `${tag} — ${obj.message}` : tag
  if (obj.type === 'error') console.warn(msg)
  else console.log(msg)
}

const _clearTimer = (id) => {
  const entry = _timers.get(id)
  if (entry?.timer) clearTimeout(entry.timer)
  _timers.delete(id)
}

const _arm = (state, id, ms) => {
  const timer = setTimeout(() => _beginExit(state, id), ms)
  _timers.set(id, {
    timer,
    state,
    startedAt: Date.now(),
    remaining: ms,
    paused: false
  })
}

// Phase 1: flip `_exiting` so the toast plays its exit animation.
// Phase 2: after EXIT_MS, drop the toast from the array.
const _beginExit = (state, id) => {
  _timers.delete(id)
  const arr = state?.notifications
  if (!arr) return
  // No-op if already removed (manual dismiss may have raced us).
  if (!arr.some((n) => n.id === id)) return
  state.update(
    { notifications: arr.map((n) => (n.id === id ? { ...n, _exiting: true } : n)) },
    { preventUpdate: false }
  )
  setTimeout(() => {
    const current = state?.notifications
    if (!current) return
    state.update(
      { notifications: current.filter((n) => n.id !== id) },
      { preventUpdate: false }
    )
    _forget(id)
  }, EXIT_MS)
}

export async function openNotification (obj, opts = {}) {
  const state = this.getRootState?.()
  if (!state) {
    _logFallback(obj)
    return
  }

  // De-dup: identical title+message already queued.
  const dup = state.notifications?.find(
    (n) => n.title === obj.title && n.message === obj.message && !n._exiting
  )
  if (dup) return

  // Chime — opt-in per call (`sound: true`), gated inside playNotifySound
  // itself on the user's notification_sound preference + a debounce, so a
  // burst of toasts never stacks a burst of chimes.
  if (obj.sound) {
    try {
      this.call('playNotifySound')
    } catch (err) {
      console.warn('[openNotification] playNotifySound failed:', err?.message || err)
    }
  }

  const id = _genId()
  const duration = Math.max(0, obj.duration || opts.duration || 3500)
  const record = { ...obj, id, _exiting: false }

  if (obj.action?.call && _canCall(this, obj.action.call)) _origins.set(id, this)

  await state.update(
    { notifications: [...(state.notifications || []), record] },
    { preventUpdate: false }
  )

  if (duration > 0) {
    _arm(state, id, duration)
  }
}

export async function openNotificationParams (title, error, message, opts = {}) {
  return await openNotification.call(this, { title, error, message }, opts)
}

// Manual dismiss — synchronous: clears timer, plays exit animation,
// then drops from the array. Accepts either the full record or a bare
// `{ id }` shape so component callers can pass `s` directly.
export function closeNotification (notification, _opts = {}) {
  const state = this.getRootState?.()
  if (!state) return
  const id = notification?.id
  if (!id) return
  _clearTimer(id)
  _beginExit(state, id)
}

// Run a toast's action — from the toast's body or its action capsule
// (`this` = the toast). The element that raised the toast runs it when it can
// (its context, `this` = that element); otherwise the toast does, as before —
// a shell action, or a record that reached the array without openNotification.
// A name that resolves nowhere warns once and does nothing; the caller then
// closes the toast.
export function runNotificationAction (notification) {
  const id = notification?.id
  const action = notification?.action
  if (!action?.call) return
  const origin = _origins.get(id) || (_canCall(this, action.call) ? this : null)
  if (!origin) {
    _warnUnknownAction(action.call)
    return
  }
  const args = Array.isArray(action.args) ? action.args : []
  try {
    return origin.call(action.call, ...args)
  } catch (err) {
    console.warn('[notification] toast action failed:', err?.message || err)
  }
}

// Pause a toast's auto-dismiss while the cursor is over it. Subtract
// elapsed-since-start from the remaining time so resume picks up where
// we left off.
export function pauseNotification (id) {
  const entry = _timers.get(id)
  if (!entry || entry.paused) return
  clearTimeout(entry.timer)
  entry.paused = true
  entry.remaining = Math.max(0, entry.remaining - (Date.now() - entry.startedAt))
}

// Resume a paused toast — re-arm the timer with whatever time was
// remaining when pauseNotification ran.
export function resumeNotification (id) {
  const entry = _timers.get(id)
  if (!entry?.paused) return
  entry.paused = false
  entry.startedAt = Date.now()
  entry.timer = setTimeout(
    () => _beginExit(entry.state, id),
    entry.remaining
  )
}
